/**
 * TaxFlow Stage 3 — Rich-Text Email Composer
 * ============================================
 * Modes: new | reply | reply_all | forward
 * Features:
 *  - TipTap rich-text editor (bold, italic, underline, lists, links, alignment)
 *  - To / Cc / Bcc token fields
 *  - From-mailbox picker (authorized only)
 *  - Subject field
 *  - Signature auto-inject per mailbox
 *  - Draft autosave (5s debounce)
 *  - Draft recovery prompt on reopen
 *  - File attachments (metadata, no binary upload yet)
 *  - Idempotent send via send_id
 *  - Send progress / status / error state
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Underline } from "@tiptap/extension-underline";
import { TextAlign } from "@tiptap/extension-text-align";
import { Color } from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import { Link } from "@tiptap/extension-link";
import { Image } from "@tiptap/extension-image";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { getToken } from "@/lib/api";
import {
  X, Send, Bold, Italic, Underline as UnderlineIcon,
  AlignLeft, AlignCenter, AlignRight, List, ListOrdered,
  Link as LinkIcon, Paperclip, ChevronDown, Loader2,
  RotateCcw, Trash2, Check, Maximize2, Minimize2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Token field (To/Cc/Bcc) ───────────────────────────────────────────────────
function TokenField({ label, value, onChange, placeholder, testId }) {
  const [input, setInput] = useState("");

  const addToken = (raw) => {
    const emails = raw.split(/[\s,;]+/).map(e => e.trim()).filter(e => e.includes("@"));
    if (emails.length) onChange([...value, ...emails.filter(e => !value.includes(e))]);
  };

  const handleKey = (e) => {
    if (["Enter", ",", ";", " ", "Tab"].includes(e.key)) {
      e.preventDefault();
      if (input.trim()) { addToken(input); setInput(""); }
    }
    if (e.key === "Backspace" && !input && value.length) {
      onChange(value.slice(0, -1));
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text");
    addToken(text);
  };

  return (
    <div className="flex items-start gap-2 py-1 border-b border-gray-100" data-testid={testId}>
      <span className="text-xs font-semibold text-gray-400 pt-2 w-8 shrink-0">{label}</span>
      <div className="flex flex-wrap gap-1 flex-1 min-h-[32px] items-center">
        {value.map((email, i) => (
          <span key={i} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded-full">
            {email}
            <button onClick={() => onChange(value.filter((_, j) => j !== i))} className="hover:text-red-500">
              <X size={10} />
            </button>
          </span>
        ))}
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKey}
          onPaste={handlePaste}
          onBlur={() => { if (input.trim()) { addToken(input); setInput(""); } }}
          placeholder={value.length === 0 ? placeholder : ""}
          className="flex-1 min-w-[160px] text-sm outline-none bg-transparent text-gray-700"
        />
      </div>
    </div>
  );
}

// ── Toolbar button ────────────────────────────────────────────────────────────
function ToolbarBtn({ onClick, active, title, children, testId }) {
  return (
    <button
      onMouseDown={e => { e.preventDefault(); onClick(); }}
      title={title}
      data-testid={testId}
      className={`p-1.5 rounded transition-colors ${active
        ? "bg-gray-200 text-gray-900"
        : "text-gray-500 hover:bg-gray-100 hover:text-gray-800"
      }`}
    >
      {children}
    </button>
  );
}

// ── Main composer ─────────────────────────────────────────────────────────────
export default function ComposerModal({
  mode = "new",         // new | reply | reply_all | forward
  conversation,         // full conversation object (for reply/forward context)
  parentMessage,        // message being replied to (for In-Reply-To)
  onClose,
  onSent,
  isInline = false,     // when true: renders inline (no fixed overlay) for use inside workspace
  onDiscard,            // inline mode: called when user clicks discard/close
  composerExpanded = false,  // Stage 3: expand mode driven by parent
  onToggleExpand,            // Stage 3: callback to toggle expand state
}) {
  // Recipients
  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [bcc, setBcc] = useState([]);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState("");

  // Mailboxes
  const [mailboxes, setMailboxes] = useState([]);
  const [selectedMailbox, setSelectedMailbox] = useState(null);
  const [mailboxOpen, setMailboxOpen] = useState(false);

  // Draft
  const [draftId, setDraftId] = useState(null);
  const [draftRecovery, setDraftRecovery] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const autosaveTimer = useRef(null);

  // Send state
  const [sendId] = useState(() => uuidv4());
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [sendSuccess, setSendSuccess] = useState(false);

  // Attachment (metadata only)
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);

  const cid = conversation?.id;

  // ── TipTap editor ──────────────────────────────────────────────────────────
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TextStyle,
      Color,
      Link.configure({ openOnClick: false }),
      Image.configure({ inline: true }),
    ],
    content: "",
    editorProps: {
      attributes: {
        class: "prose max-w-none outline-none text-sm text-gray-800 min-h-[180px] p-3",
      },
      handlePaste(view, event) {
        const items = Array.from(event.clipboardData?.items || []);
        const imageItem = items.find(i => i.type.startsWith("image/"));
        if (imageItem) {
          const file = imageItem.getAsFile();
          const reader = new FileReader();
          reader.onload = (e) => {
            view.dispatch(
              view.state.tr.replaceSelectionWith(
                view.state.schema.nodes.image.create({ src: e.target.result })
              )
            );
          };
          reader.readAsDataURL(file);
          return true;
        }
        return false;
      },
    },
  });

  // ── Load authorized mailboxes ──────────────────────────────────────────────
  useEffect(() => {
    axios.get(`${API}/api/titan/smtp/authorized-mailboxes`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    }).then(res => {
      const mbs = res.data.mailboxes || [];
      setMailboxes(mbs);
      if (mbs.length > 0) {
        // Default to conversation's mailbox if available
        const convMailbox = conversation?.mailbox_key;
        const match = convMailbox ? mbs.find(m => m.mailbox_key === convMailbox) : null;
        setSelectedMailbox(match || mbs[0]);
      }
    }).catch(() => {});
  }, [conversation]);

  // ── Inject signature when mailbox changes ─────────────────────────────────
  useEffect(() => {
    if (!editor || !selectedMailbox) return;
    const sig = selectedMailbox.signature || "";
    if (!sig) return;
    // Only inject signature if editor is mostly empty (new compose)
    const currentContent = editor.getHTML();
    if (currentContent === "<p></p>" || currentContent === "") {
      editor.commands.setContent(`<p></p>${sig}`);
    }
  }, [selectedMailbox, editor]);

  // ── Pre-fill for reply / forward ──────────────────────────────────────────
  useEffect(() => {
    if (!editor || !parentMessage) return;
    const contact = conversation?.contact_name || conversation?.contact_email || "";
    const contactEmail = conversation?.contact_email || parentMessage?.from_address || "";
    const convSubject = conversation?.subject || "";

    if (mode === "reply") {
      setTo([contactEmail].filter(Boolean));
      setSubject(convSubject.startsWith("Re: ") ? convSubject : `Re: ${convSubject}`);
    } else if (mode === "reply_all") {
      const existing = [contactEmail, ...(parentMessage?.cc || [])].filter(Boolean);
      setTo(existing);
      setCc([]);
      setShowCc(existing.length > 0);
      setSubject(convSubject.startsWith("Re: ") ? convSubject : `Re: ${convSubject}`);
    } else if (mode === "forward") {
      setSubject(convSubject.startsWith("Fwd: ") ? convSubject : `Fwd: ${convSubject}`);
      const fwdBody = `<br><br>---------- Forwarded message ----------<br>
<strong>From:</strong> ${contact || contactEmail}<br>
<strong>Subject:</strong> ${convSubject}<br>
<br>${parentMessage?.html_body || parentMessage?.body || ""}`;
      editor.commands.setContent(fwdBody);
    }
  }, [mode, parentMessage, conversation, editor]);

  // ── Draft recovery: check for existing draft ──────────────────────────────
  useEffect(() => {
    if (!cid) return;
    axios.get(`${API}/api/communications/conversations/${cid}/drafts`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    }).then(res => {
      const draft = res.data.draft;
      if (draft && draft.id) setDraftRecovery(draft);
    }).catch(() => {});
  }, [cid]);

  const recoverDraft = () => {
    if (!draftRecovery || !editor) return;
    setTo(draftRecovery.to || []);
    setCc(draftRecovery.cc || []);
    setSubject(draftRecovery.subject || "");
    editor.commands.setContent(draftRecovery.html_body || "");
    setDraftId(draftRecovery.id);
    setDraftRecovery(null);
    if (draftRecovery.cc?.length) setShowCc(true);
  };

  const discardDraftRecovery = () => setDraftRecovery(null);

  // ── Autosave draft ─────────────────────────────────────────────────────────
  const saveDraft = useCallback(() => {
    if (!cid || !editor) return;
    const html = editor.getHTML();
    if (!html || html === "<p></p>") return;
    axios.post(
      `${API}/api/communications/conversations/${cid}/drafts`,
      {
        mailbox_key: selectedMailbox?.mailbox_key || null,
        to, cc, subject,
        html_body: html,
        in_reply_to: parentMessage?.rfc_message_id || null,
        references: parentMessage?.rfc_references || [],
        mode,
        parent_message_id: parentMessage?.id || null,
      },
      { headers: { Authorization: `Bearer ${getToken()}` } }
    ).then(res => {
      setDraftId(res.data.draft_id);
      setSavedAt(new Date());
    }).catch(() => {});
  }, [cid, editor, selectedMailbox, to, cc, subject, parentMessage, mode]);

  // Debounced autosave
  useEffect(() => {
    if (!editor) return;
    const unsubscribe = editor.on("update", () => {
      clearTimeout(autosaveTimer.current);
      autosaveTimer.current = setTimeout(saveDraft, 5000);
    });
    return () => {
      clearTimeout(autosaveTimer.current);
      if (unsubscribe && typeof unsubscribe === "function") unsubscribe();
    };
  }, [editor, saveDraft]);

  // ── File attachment (with base64 content reading) ─────────────────────────
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const allowed = ["application/pdf", "image/png", "image/jpeg", "image/gif", "image/webp",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/plain", "text/csv"];
    const MAX_MB = 10;
    setSendError(null);
    for (const f of files) {
      if (!allowed.includes(f.type) && !f.name.match(/\.(pdf|png|jpg|jpeg|gif|webp|doc|docx|xls|xlsx|txt|csv)$/i)) {
        setSendError(`File type not allowed: ${f.name}`);
        return;
      }
      if (f.size > MAX_MB * 1024 * 1024) {
        setSendError(`File too large (max ${MAX_MB}MB): ${f.name}`);
        return;
      }
      // Read as base64
      const reader = new FileReader();
      reader.onload = (ev) => {
        const dataUrl = ev.target.result; // data:<type>;base64,<data>
        const base64 = dataUrl.split(",")[1] || "";
        setAttachments(prev => [
          ...prev,
          { name: f.name, size: f.size, type: f.type, content_base64: base64 }
        ]);
      };
      reader.readAsDataURL(f);
    }
    e.target.value = "";
  };

  // ── Set link ───────────────────────────────────────────────────────────────
  const setLink = () => {
    const url = prompt("Enter link URL:");
    if (url) editor?.chain().focus().setLink({ href: url }).run();
  };

  // ── Send ───────────────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (sending || sendSuccess) return;
    setSendError(null);

    if (!to.length) { setSendError("At least one To: recipient is required."); return; }
    if (!subject.trim()) { setSendError("Subject is required."); return; }
    if (!selectedMailbox) { setSendError("Please select a sending mailbox."); return; }
    if (!selectedMailbox.credential_present) {
      setSendError(`SMTP credential for ${selectedMailbox.display_label} is not configured.`);
      return;
    }

    // Recipient safety: placeholder/test domains must never be actual send destinations.
    const PLACEHOLDER_DOMAINS = ["@example.com", "@test.com", "@localhost"];
    const suspiciousRecipients = to.filter(addr =>
      PLACEHOLDER_DOMAINS.some(d => addr.toLowerCase().includes(d))
    );
    if (suspiciousRecipients.length > 0) {
      setSendError(
        `Cannot send to test or placeholder address: ${suspiciousRecipients.join(", ")}. ` +
        "Please replace with a verified recipient before sending."
      );
      return;
    }

    const html = editor?.getHTML() || "";
    if (!html || html === "<p></p>") { setSendError("Email body is required."); return; }

    if (!cid) { setSendError("No conversation linked to this email."); return; }

    setSending(true);
    try {
      await axios.post(
        `${API}/api/communications/conversations/${cid}/send-email`,
        {
          mailbox_key: selectedMailbox.mailbox_key,
          to, cc, bcc, subject,
          html_body: html,
          in_reply_to: parentMessage?.rfc_message_id || null,
          references: parentMessage?.rfc_references || [],
          send_id: sendId,
          draft_id: draftId || null,
          attachments: attachments.map(a => ({
            filename: a.name,
            content_type: a.type,
            content_base64: a.content_base64 || "",
            size_bytes: a.size,
          })),
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setSendSuccess(true);
      setTimeout(() => {
        onSent?.();
        onClose?.();
      }, 1200);
    } catch (err) {
      setSendError(err?.response?.data?.detail || "Failed to send email. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (!sendSuccess && editor) {
      clearTimeout(autosaveTimer.current);
      saveDraft();
    }
    if (isInline) { onDiscard?.(); } else { onClose?.(); }
  };

  const inReplyTo = parentMessage?.rfc_message_id || null;

  if (!editor) return null;

  const innerContent = (
    <div
      className={isInline ? "flex flex-col bg-white" : "w-full max-w-3xl bg-white rounded-xl shadow-2xl flex flex-col max-h-[92vh]"}
      data-testid="composer-modal"
    >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 shrink-0 bg-white">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-800">
              {mode === "new" ? "New Email"
                : mode === "forward" ? "Forward"
                : "Reply"}
            </span>
            {savedAt && (
              <span className="text-xs text-gray-400">
                · Saved {savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {draftId && (
              <button
                onClick={() => {
                  if (!cid) return;
                  axios.delete(`${API}/api/communications/drafts/${draftId}`, {
                    headers: { Authorization: `Bearer ${getToken()}` },
                  }).then(() => { setDraftId(null); setSavedAt(null); });
                }}
                className="text-xs text-gray-400 hover:text-red-500 px-2 py-1 flex items-center gap-1 rounded hover:bg-red-50"
                data-testid="composer-discard-draft-btn"
                title="Delete this saved draft"
              >
                <Trash2 size={13} /> Delete Draft
              </button>
            )}
            {isInline && onToggleExpand && (
              <button
                onClick={onToggleExpand}
                className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded"
                data-testid="composer-expand-btn"
                title={composerExpanded ? "Collapse composer" : "Expand composer"}
              >
                {composerExpanded ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
              </button>
            )}
            <button
              onClick={handleClose}
              className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded"
              data-testid="composer-close-btn"
              title="Save draft and close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Draft recovery banner */}
        {draftRecovery && (
          <div
            className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center justify-between gap-3"
            data-testid="composer-draft-recovery-banner"
          >
            <div className="flex items-center gap-1.5 min-w-0">
              <RotateCcw size={12} className="text-amber-600 shrink-0" />
              <span className="text-xs text-amber-800 font-medium">
                Previous draft from {new Date(draftRecovery.updated_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
              <span className="text-xs text-amber-600">— your current work will be replaced</span>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                onClick={recoverDraft}
                className="text-xs bg-amber-600 text-white px-2.5 py-1 rounded hover:bg-amber-700 font-medium"
                data-testid="composer-restore-draft-btn"
              >
                Restore
              </button>
              <button
                onClick={discardDraftRecovery}
                className="text-xs text-amber-600 hover:underline px-1"
                data-testid="composer-discard-recovered-draft-btn"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {/* Mailbox picker */}
          <div className="px-4 py-2 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-400 w-8 shrink-0">From</span>
              <div className="relative">
                <button
                  onClick={() => setMailboxOpen(o => !o)}
                  className="flex items-center gap-1 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded px-2 py-1 hover:bg-gray-100"
                  data-testid="composer-mailbox-picker"
                >
                  {selectedMailbox ? (
                    <>
                      <span className="font-medium">{selectedMailbox.display_label}</span>
                      <span className="text-gray-400 text-xs">({selectedMailbox.email_address})</span>
                      {!selectedMailbox.credential_present && (
                        <span className="text-red-500 text-xs ml-1">[unconfigured]</span>
                      )}
                    </>
                  ) : "Select mailbox"}
                  <ChevronDown size={12} />
                </button>
                {mailboxOpen && (
                  <div
                    className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[260px]"
                    data-testid="composer-mailbox-dropdown"
                  >
                    {mailboxes.map(mb => (
                      <button
                        key={mb.mailbox_key}
                        onClick={() => { setSelectedMailbox(mb); setMailboxOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${selectedMailbox?.mailbox_key === mb.mailbox_key ? "bg-blue-50 text-blue-700" : ""}`}
                        data-testid={`composer-mailbox-option-${mb.mailbox_key}`}
                      >
                        <div className="font-medium">{mb.display_label}</div>
                        <div className="text-xs text-gray-400">{mb.email_address}</div>
                        {!mb.credential_present && <div className="text-xs text-red-400">SMTP not configured</div>}
                      </button>
                    ))}
                    {mailboxes.length === 0 && (
                      <div className="px-3 py-2 text-xs text-gray-400">No authorized mailboxes available.</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* To / Cc / Bcc */}
          <div className="px-4">
            <TokenField
              label="To"
              value={to}
              onChange={setTo}
              placeholder="recipient@example.com"
              testId="composer-to-field"
            />
            <div className="flex items-center gap-2 py-0.5">
              {!showCc && (
                <button
                  onClick={() => setShowCc(true)}
                  className="text-xs text-blue-500 hover:underline"
                  data-testid="composer-show-cc-btn"
                >Cc</button>
              )}
              {!showBcc && (
                <button
                  onClick={() => setShowBcc(true)}
                  className="text-xs text-blue-500 hover:underline ml-2"
                  data-testid="composer-show-bcc-btn"
                >Bcc</button>
              )}
            </div>
            {showCc && (
              <TokenField label="Cc" value={cc} onChange={setCc} placeholder="cc@example.com" testId="composer-cc-field" />
            )}
            {showBcc && (
              <TokenField label="Bcc" value={bcc} onChange={setBcc} placeholder="bcc@example.com" testId="composer-bcc-field" />
            )}
          </div>

          {/* Subject */}
          <div className="px-4 py-1.5 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-400 w-8 shrink-0">Sub</span>
              <input
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="Subject"
                className="flex-1 text-sm outline-none text-gray-800 bg-transparent"
                data-testid="composer-subject-field"
              />
            </div>
          </div>

          {/* Formatting toolbar */}
          <div className="px-3 py-1.5 border-b border-gray-100 flex items-center gap-0.5 flex-wrap">
            <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold" testId="toolbar-bold"><Bold size={14} /></ToolbarBtn>
            <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic" testId="toolbar-italic"><Italic size={14} /></ToolbarBtn>
            <ToolbarBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline" testId="toolbar-underline"><UnderlineIcon size={14} /></ToolbarBtn>
            <div className="w-px h-4 bg-gray-200 mx-1" />
            <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Align left" testId="toolbar-align-left"><AlignLeft size={14} /></ToolbarBtn>
            <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Align center" testId="toolbar-align-center"><AlignCenter size={14} /></ToolbarBtn>
            <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="Align right" testId="toolbar-align-right"><AlignRight size={14} /></ToolbarBtn>
            <div className="w-px h-4 bg-gray-200 mx-1" />
            <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list" testId="toolbar-bullet-list"><List size={14} /></ToolbarBtn>
            <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Ordered list" testId="toolbar-ordered-list"><ListOrdered size={14} /></ToolbarBtn>
            <div className="w-px h-4 bg-gray-200 mx-1" />
            <ToolbarBtn onClick={setLink} active={editor.isActive("link")} title="Insert link" testId="toolbar-link"><LinkIcon size={14} /></ToolbarBtn>
            <div className="w-px h-4 bg-gray-200 mx-1" />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800 rounded"
              title="Attach file"
              data-testid="toolbar-attach-file"
            >
              <Paperclip size={14} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileChange}
              data-testid="composer-file-input"
              accept=".pdf,.png,.jpg,.jpeg,.gif,.doc,.docx,.xls,.xlsx,.txt,.csv"
            />
          </div>

          {/* Editor body */}
          <div
            className="relative"
            onClick={() => editor?.commands.focus()}
            data-testid="composer-editor-body"
          >
            <EditorContent editor={editor} />
          </div>

          {/* Attachments preview */}
          {attachments.length > 0 && (
            <div className="px-4 pb-2 flex flex-wrap gap-2" data-testid="composer-attachments">
              {attachments.map((f, i) => (
                <div key={i} className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded px-2 py-1 text-xs text-gray-600">
                  <Paperclip size={11} />
                  <span>{f.name}</span>
                  <span className="text-gray-400">({(f.size / 1024).toFixed(1)}KB)</span>
                  <button onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))} className="hover:text-red-500 ml-1">
                    <X size={10} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Error banner */}
        {sendError && (
          <div
            className="mx-4 mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 flex items-center justify-between"
            data-testid="composer-send-error"
          >
            {sendError}
            <button onClick={() => setSendError(null)} className="ml-2 text-red-400 hover:text-red-600">
              <X size={12} />
            </button>
          </div>
        )}

        {/* Footer / send bar */}
        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between shrink-0">
          <div className="text-xs text-gray-400">
            {inReplyTo ? (
              <span>Replying to thread</span>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleClose}
              className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700"
              data-testid="composer-cancel-btn"
            >
              {isInline ? "Close" : "Cancel"}
            </button>
            <button
              onClick={handleSend}
              disabled={sending || sendSuccess || !to.length || !subject.trim() || !selectedMailbox}
              data-testid="composer-send-btn"
              className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-all
                ${sendSuccess
                  ? "bg-green-500 text-white"
                  : sending
                  ? "bg-blue-400 text-white cursor-not-allowed"
                  : "bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                }`}
            >
              {sendSuccess ? (
                <><Check size={14} /> Sent</>
              ) : sending ? (
                <><Loader2 size={14} className="animate-spin" /> Sending...</>
              ) : (
                <><Send size={14} /> Send</>
              )}
            </button>
          </div>
        </div>
      </div>
  );

  if (isInline) return innerContent;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-2 sm:p-4"
      data-testid="composer-modal-overlay"
    >
      {innerContent}
    </div>
  );
}
